(function() {
    if (!customElements.get('collection-slider')) {
      class CollectionSlider extends HTMLElement {
        constructor() {
          super();
          // Extract slider ID from class name
          var classList = Array.from(this.classList);
          var sliderIdMatch = classList.find(function(cls) { return cls.startsWith('ai-collection-slider-'); });
          this.sliderId = sliderIdMatch ? sliderIdMatch.replace('ai-collection-slider-', '') : '';

          // Find elements with unique IDs first, fallback to generic selectors
          var idSuffix = this.sliderId ? '-' + this.sliderId : '';
          this.track         = this.querySelector('.ai-collection-slider-track' + idSuffix) || this.querySelector('.ai-collection-slider-track');
          this.prevButton    = this.querySelector('.ai-collection-slider-prev' + idSuffix) || this.querySelector('.ai-collection-slider-prev');
          this.nextButton    = this.querySelector('.ai-collection-slider-next' + idSuffix) || this.querySelector('.ai-collection-slider-next');
          this.navContainer  = this.querySelector('.ai-collection-slider-nav' + idSuffix) || this.querySelector('.ai-collection-slider-nav');
          this.dotsContainer = this.querySelector('.ai-collection-slider-dots' + idSuffix) || this.querySelector('.ai-collection-slider-dots');
          // Comma-separated selector was always selecting `.ai-collection-card` (no suffix) too, picking up cards from other slider instances. Prefer the id-suffixed selector; fall back to the bare class only when the suffixed selector finds nothing.
          this.cards = Array.from(this.querySelectorAll('.ai-collection-card' + idSuffix));
          if (this.cards.length === 0) this.cards = Array.from(this.querySelectorAll('.ai-collection-card'));
          this.currentIndex  = 0;
          this.autoplayInterval = null;
          this.autoplayEnabled  = this.dataset.autoplay === 'true';
          this.autoplaySpeed    = Math.max(2, parseInt(this.dataset.autoplaySpeed || '5', 10)) * 1000;
          this._imgListeners   = [];
          this.activeCardIndex = 0;
          this.intersectionObserver = null;
          this.cardVisibilityMap = new Map();

          var self = this;
          this._debounce = function(fn, wait){
            var t; wait = wait || 200;
            return function(){ var a=arguments; clearTimeout(t); t=setTimeout(function(){ fn.apply(self, a); }, wait); };
          };

          this._handleResize = this._debounce(function(){
            self.rebuildDots();
            self.syncFromScroll();
            self.updateButtons();
            self.adjustCardSizing();
            // Toggle active card behavior based on screen size
            var isMobileNow = self.isMobile();
            if (isMobileNow && !self.intersectionObserver) {
              self.initActiveCardBehavior();
            } else if (!isMobileNow && self.intersectionObserver) {
              self.cleanupActiveCardBehavior();
              // Reset all cards to full opacity on desktop
              self.cards.forEach(function(card) {
                card.classList.remove('is-active');
              });
            }
          }, 250);

          this._mo = new MutationObserver(function(){
            // Same suffix-first / generic-fallback pattern as initial cards lookup so MutationObserver re-scans don't bleed across slider instances.
            self.cards = Array.from(self.querySelectorAll('.ai-collection-card' + idSuffix));
            if (self.cards.length === 0) self.cards = Array.from(self.querySelectorAll('.ai-collection-card'));
            self.rebuildDots();
            self.ensureIndexInBounds();
            self.updateButtons();
            self.updateDots();
            self.adjustCardSizing();
            // Reinitialize active card behavior if cards changed
            if (self.intersectionObserver) {
              self.cleanupActiveCardBehavior();
              self.initActiveCardBehavior();
            }
          });
        }

        connectedCallback() {
          if (!this.track || this.cards.length === 0) return;

          // Read per-instance from data attribute - the class definition is
          // shared via customElements.define so we cannot bake in Liquid here.
          var navVisible = this.dataset.showNavigation === 'true';
          if (navVisible) this.rebuildDots();

          this.setupEventListeners();
          this.attachImageLoadListeners();
          this.syncFromScroll();
          this.updateButtons();
          this.adjustCardSizing();
          var selfInit = this;
          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
              selfInit.adjustCardSizing();
            });
          });

          // Only enable active card behavior on mobile
          if (this.isMobile()) {
            this.initActiveCardBehavior();
          }

          if (this.autoplayEnabled && !this.prefersReducedMotion()) this.startAutoplay();

          this._mo.observe(this.track, { childList: true, subtree: true });
        }

        disconnectedCallback() {
          this.stopAutoplay();
          this._mo.disconnect();
          this.detachImageLoadListeners();
          this.cleanupActiveCardBehavior();
          document.removeEventListener('visibilitychange', this._visibilityHandler);
          window.removeEventListener('resize', this._handleResize);
          if (this.track && this._scrollHandler) this.track.removeEventListener('scroll', this._scrollHandler);
          if (this._focusHandler)  this.removeEventListener('focusin', this._focusHandler);
          if (this._blurHandler)   this.removeEventListener('focusout', this._blurHandler);
        }

        prefersReducedMotion() {
          try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
          catch(e){ return false; }
        }

        isMobile() {
          return window.innerWidth <= 767;
        }

        rebuildDots() {
          if (!this.dotsContainer) return;
          this.dotsContainer.innerHTML = '';
          var totalDots = this.getTotalDots();
          for (var i = 0; i < totalDots; i++){
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'ai-collection-slider-dot' + (this.sliderId ? ' ai-collection-slider-dot-' + this.sliderId : '');
            dot.setAttribute('aria-label', 'Slide ' + (i + 1));
            dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
            if (i === this.currentIndex) dot.classList.add('active');
            var self = this;
            (function(index){
              dot.addEventListener('click', function(){
                self.goToSlide(index);
              });
            })(i);
            this.dotsContainer.appendChild(dot);
          }
        }

        getCardsPerView() {
          var w = window.innerWidth;
          if (w <= 767) {
            if (w < 400) return 1.5;
            if (w < 550) return 2.5;
            return 3.5;
          }
          return 5;
        }

        getStep() {
          return 1;
        }

        getTotalDots() {
          var step = this.getStep();
          return Math.max(1, Math.ceil(this.cards.length / step));
        }

        setupEventListeners() {
          var self = this;

          if (this.prevButton) this.prevButton.addEventListener('click', function(){ self.stopAutoplay(); self.prev(); });
          if (this.nextButton) this.nextButton.addEventListener('click', function(){ self.stopAutoplay(); self.next(); });

          this.addEventListener('pointerdown', function(){ self.stopAutoplay(); }, { passive: true });
          this.addEventListener('mouseenter', function(){ self.stopAutoplay(); });
          this.addEventListener('mouseleave', function(){ if (self.autoplayEnabled && !self.prefersReducedMotion()) self.startAutoplay(); });

          this._focusHandler = function(){ self.stopAutoplay(); };
          this._blurHandler  = function(){ if (self.autoplayEnabled && !self.prefersReducedMotion()) self.startAutoplay(); };
          this.addEventListener('focusin', this._focusHandler);
          this.addEventListener('focusout', this._blurHandler);

          this.addEventListener('keydown', function(e){
            if (e.key === 'ArrowLeft') { e.preventDefault(); self.stopAutoplay(); self.prev(); }
            if (e.key === 'ArrowRight'){ e.preventDefault(); self.stopAutoplay(); self.next(); }
            if (e.key === 'Home')      { e.preventDefault(); self.stopAutoplay(); self.goToSlide(0); }
            if (e.key === 'End')       { e.preventDefault(); self.stopAutoplay(); self.goToSlide(self.getTotalDots()-1); }
          });

          this._scrollHandler = this._debounce(function(){ self.updateActiveState(); }, 80);
          if (this.track) this.track.addEventListener('scroll', this._scrollHandler, { passive: true });

          window.addEventListener('resize', this._handleResize);

          this._visibilityHandler = function(){
            if (document.hidden) self.stopAutoplay();
            else if (self.autoplayEnabled && !self.prefersReducedMotion()) self.startAutoplay();
          };
          document.addEventListener('visibilitychange', this._visibilityHandler, { passive: true });
        }

        attachImageLoadListeners() {
          var self = this;
          var imgs = this.track ? Array.from(this.track.querySelectorAll('img')) : [];
          this._imgListeners = imgs.map(function(img){
            var handler = function(){
              self.syncFromScroll();
              self.rebuildDots();
              self.updateButtons();
              self.adjustCardSizing();
            };
            img.addEventListener('load', handler, { once: true });
            return { img: img, handler: handler };
          });
        }

        detachImageLoadListeners() {
          this._imgListeners.forEach(function(pair){
            try { pair.img.removeEventListener('load', pair.handler); } catch(e){}
          });
          this._imgListeners = [];
        }

        ensureIndexInBounds() {
          var maxIdx = this.getTotalDots() - 1;
          if (this.currentIndex > maxIdx) this.currentIndex = maxIdx;
          if (this.currentIndex < 0) this.currentIndex = 0;
        }

        prev() {
          this.ensureIndexInBounds();
          var maxIndex = this.getTotalDots() - 1;

          if (this.currentIndex > 0) {
            this.currentIndex--;
          } else {
            this.currentIndex = maxIndex;
          }

          this.scrollToIndex(this.currentIndex * this.getStep());
          this.updateButtons();
          this.updateDots();
        }

        next() {
          this.ensureIndexInBounds();
          var maxIndex = this.getTotalDots() - 1;
          if (this.currentIndex < maxIndex) {
            this.currentIndex++;
            this.scrollToIndex(this.currentIndex * this.getStep());
          } else {
            this.currentIndex = 0;
            this.scrollToIndex(0);
          }
          this.updateButtons();
          this.updateDots();
        }

        goToSlide(index) {
          this.stopAutoplay();
          this.currentIndex = Math.max(0, Math.min(index, this.getTotalDots() - 1));
          this.scrollToIndex(this.currentIndex * this.getStep());
          this.updateButtons();
          this.updateDots();
        }

        scrollToIndex(cardIndex) {
          var idx = Math.max(0, Math.floor(cardIndex));
          var target = this.cards[idx];
          if (!target) return;
          /* Mobile uses `scroll-snap-align: center`, so the target offset
             needs to subtract the half-gutter for the card to actually
             land centered. Desktop still snaps to start. */
          var left = target.offsetLeft;
          if (this.isMobile()) {
            left = target.offsetLeft - (this.track.clientWidth - target.offsetWidth) / 2;
            if (left < 0) left = 0;
          }
          this.track.scrollTo({ left: left, behavior: 'smooth' });
        }

        updateButtons() {
          if (this.prevButton) this.prevButton.disabled = false;
          if (this.nextButton) this.nextButton.disabled = false;
        }

        updateDots() {
          if (!this.dotsContainer) return;
          var dotSelector = this.sliderId
            ? '.ai-collection-slider-dot-' + this.sliderId + ', .ai-collection-slider-dot'
            : '.ai-collection-slider-dot';
          var dots = this.dotsContainer.querySelectorAll(dotSelector);
          if (!dots || !dots.length) return;
          for (var i = 0; i < dots.length; i++){
            if (i === this.currentIndex) {
              dots[i].classList.add('active');
              dots[i].setAttribute('aria-current', 'true');
            } else {
              dots[i].classList.remove('active');
              dots[i].setAttribute('aria-current', 'false');
            }
          }
        }

        syncFromScroll() {
          var step = this.getStep();
          var card = this.cards[0];
          if (!card) return;
          var w = window.innerWidth;
          var gap = (w <= 989)
            ? parseInt(this.dataset.mobileGap || '12', 10)
            : parseInt(this.dataset.cardGap || '20', 10);
          var cardWidth = card.offsetWidth;
          var groupWidth = (cardWidth + gap) * step;
          var idx = groupWidth > 0 ? Math.round(this.track.scrollLeft / groupWidth) : 0;
          this.currentIndex = Math.max(0, Math.min(idx, this.getTotalDots() - 1));
          this.updateDots();
        }

        updateActiveState() {
          this.syncFromScroll();
          this.updateButtons();
        }

        initActiveCardBehavior() {
          if (!this.track || this.cards.length === 0) return;
          // Guard against double-init: clean up any prior listeners/observers
          // before registering new ones so we don't leak handlers.
          if (this.intersectionObserver || this._activeCardScrollHandler) {
            this.cleanupActiveCardBehavior();
          }

          var self = this;

          // Initialize all cards as inactive
          this.cards.forEach(function(card, index) {
            if (index === 0) {
              card.classList.add('is-active');
              self.activeCardIndex = 0;
            } else {
              card.classList.remove('is-active');
            }
          });

          // Setup Intersection Observer
          var observerOptions = {
            root: this.track,
            rootMargin: '0px',
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
          };

          this.intersectionObserver = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
              var card = entry.target;
              var index = self.cards.indexOf(card);
              if (index !== -1) {
                var ratio = entry.intersectionRatio;
                self.cardVisibilityMap.set(index, {
                  ratio: ratio,
                  isIntersecting: entry.isIntersecting,
                  boundingClientRect: entry.boundingClientRect,
                  rootBounds: entry.rootBounds
                });
              }
            });
            self.updateActiveCardFromVisibility();
          }, observerOptions);

          // Observe all cards
          this.cards.forEach(function(card) {
            self.intersectionObserver.observe(card);
          });

          // Also listen to scroll for immediate updates
          var scrollHandler = this._debounce(function() {
            self.updateActiveCardFromScroll();
          }, 50);
          this.track.addEventListener('scroll', scrollHandler, { passive: true });
          this._activeCardScrollHandler = scrollHandler;

          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
              if (!self.track || !self.isMobile()) return;
              self.updateActiveCardFromScroll();
            });
          });
        }

        updateActiveCardFromVisibility() {
          if (this.cards.length === 0 || !this.track) return;

          /* Start of strip: with m-cpv 1.5, "center wins" logic wrongly favors the peek card. */
          if (this.track.scrollLeft <= 2) {
            if (this.activeCardIndex !== 0) this.setActiveCard(0);
            return;
          }

          var bestCardIndex = -1;
          var bestVisibility = -1;
          var bestDistanceToCenter = Infinity;

          this.cardVisibilityMap.forEach(function(data, index) {
            if (data.isIntersecting && data.ratio > 0) {
              var cardRect = data.boundingClientRect;
              var rootRect = data.rootBounds;
              var cardCenter = cardRect.left + (cardRect.width / 2);
              var rootCenter = rootRect.left + (rootRect.width / 2);
              var distanceToCenter = Math.abs(cardCenter - rootCenter);

              if (data.ratio > bestVisibility) {
                bestVisibility = data.ratio;
                bestDistanceToCenter = distanceToCenter;
                bestCardIndex = index;
              } else if (Math.abs(data.ratio - bestVisibility) <= 0.06) {
                if (distanceToCenter < bestDistanceToCenter - 0.5) {
                  bestDistanceToCenter = distanceToCenter;
                  bestCardIndex = index;
                } else if (Math.abs(distanceToCenter - bestDistanceToCenter) <= 1 && index < bestCardIndex) {
                  bestCardIndex = index;
                }
              }
            }
          });

          if (bestCardIndex === -1 && this.cards.length > 0) {
            var trackRect = this.track.getBoundingClientRect();
            var trackCenter = trackRect.left + (trackRect.width / 2);
            bestCardIndex = 0;
            bestDistanceToCenter = Infinity;
            for (var i = 0; i < this.cards.length; i++) {
              var cRect = this.cards[i].getBoundingClientRect();
              var cCenter = cRect.left + (cRect.width / 2);
              var d = Math.abs(cCenter - trackCenter);
              if (d < bestDistanceToCenter - 0.5) {
                bestDistanceToCenter = d;
                bestCardIndex = i;
              } else if (Math.abs(d - bestDistanceToCenter) <= 0.5 && i < bestCardIndex) {
                bestCardIndex = i;
              }
            }
          }

          if (bestCardIndex !== -1 && bestCardIndex !== this.activeCardIndex) {
            this.setActiveCard(bestCardIndex);
          }
        }

        updateActiveCardFromScroll() {
          if (this.cards.length === 0 || !this.track) return;

          if (this.track.scrollLeft <= 2) {
            if (this.activeCardIndex !== 0) this.setActiveCard(0);
            return;
          }

          var trackRect = this.track.getBoundingClientRect();
          var trackCenter = trackRect.left + (trackRect.width / 2);
          var bestCardIndex = 0;
          var bestDistance = Infinity;
          var bestVisibility = 0;

          for (var i = 0; i < this.cards.length; i++) {
            var cardRect = this.cards[i].getBoundingClientRect();
            var intersectionLeft = Math.max(cardRect.left, trackRect.left);
            var intersectionRight = Math.min(cardRect.right, trackRect.right);
            var intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);
            var visibilityRatio = cardRect.width > 0 ? intersectionWidth / cardRect.width : 0;
            var cardCenter = cardRect.left + (cardRect.width / 2);
            var distanceToCenter = Math.abs(cardCenter - trackCenter);

            if (visibilityRatio > bestVisibility) {
              bestVisibility = visibilityRatio;
              bestDistance = distanceToCenter;
              bestCardIndex = i;
            } else if (Math.abs(visibilityRatio - bestVisibility) <= 0.06) {
              if (distanceToCenter < bestDistance - 0.5) {
                bestDistance = distanceToCenter;
                bestCardIndex = i;
              } else if (Math.abs(distanceToCenter - bestDistance) <= 1 && i < bestCardIndex) {
                bestCardIndex = i;
              }
            }
          }

          if (bestCardIndex !== this.activeCardIndex) {
            this.setActiveCard(bestCardIndex);
          }
        }

        setActiveCard(index) {
          if (index < 0 || index >= this.cards.length) return;

          // Remove active class from all cards
          this.cards.forEach(function(card) {
            card.classList.remove('is-active');
          });

          // Add active class to selected card
          this.cards[index].classList.add('is-active');
          this.activeCardIndex = index;
        }

        cleanupActiveCardBehavior() {
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
          }
          if (this.track && this._activeCardScrollHandler) {
            this.track.removeEventListener('scroll', this._activeCardScrollHandler);
            this._activeCardScrollHandler = null;
          }
          this.cardVisibilityMap.clear();
        }

        updateNavVisibility() {
          if (!this.navContainer || !this.track) return;
          var epsilon = 2;
          var needsScroll = this.track.scrollWidth - this.track.clientWidth > epsilon;
          this.navContainer.classList.toggle('ai-collection-slider-nav--no-overflow', !needsScroll);
        }

        adjustCardSizing() {
          if (!this.track || this.cards.length === 0) {
            this.updateNavVisibility();
            return;
          }

          if (!this.isMobile()) {
            var cardCount = this.cards.length;
            if (cardCount < 5) {
              this.track.classList.add('has-less-than-5-cards');
              this.track.style.setProperty('--card-count', cardCount.toString());
            } else {
              this.track.classList.remove('has-less-than-5-cards');
              this.track.style.removeProperty('--card-count');
            }
          }

          this.updateNavVisibility();
        }

        startAutoplay() {
          if (this.autoplayInterval) return;
          if (!this.autoplayEnabled) return;
          if (this.prefersReducedMotion()) return;

          var self = this;
          this.autoplayInterval = setInterval(function(){
            var maxIndex = self.getTotalDots() - 1;
            if (self.currentIndex >= maxIndex) self.currentIndex = 0;
            else self.currentIndex++;
            self.scrollToIndex(self.currentIndex * self.getStep());
            self.updateDots();
            self.updateButtons();
          }, this.autoplaySpeed);
        }

        stopAutoplay() {
          if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
          }
        }
      }

      customElements.define('collection-slider', CollectionSlider);
    }
  })();
